/**
 * Reprodução do loop — CONTRATO: `play()`, `pause()` e `isPlaying()` são
 * usados pela exportação (pausa antes de gravar e restaura depois), assim
 * como a trava `setExportLock()` / `isExportLocked()` / `useExportLock()`.
 *
 * O relógio só avança para o próximo quadro quando ele já está decodificado
 * em todas as camadas visíveis; enquanto não está, a reprodução "espera"
 * (`buffering`) em vez de pular ou piscar.
 */
import { useTimeStore } from '../state/time';
import { PLAYING_KEY } from '../state/urlSync';
import { nextPlaybackIndex } from './frames';
import { isFrameReady, isFrameUnavailable } from './preloader';
import { useLoopStore } from './store';

let timer: ReturnType<typeof setTimeout> | null = null;
let direction: 1 | -1 = 1;

const BUFFER_POLL_MS = 80;

function clearTimer(): void {
  if (timer) clearTimeout(timer);
  timer = null;
}

function setBuffering(buffering: boolean): void {
  if (useLoopStore.getState().buffering !== buffering) useLoopStore.setState({ buffering });
}

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
let waitingVisible = false;

/**
 * Aba oculta: o navegador não desenha, mas cada passo do relógio ainda
 * decodificava quadros, sincronizava caches e re-renderizava a interface —
 * processador gasto sem ninguém ver. O relógio espera a aba voltar (a
 * animação continua "tocando"; retoma no instante em que a aba aparece).
 */
function waitVisible(): void {
  if (waitingVisible) return;
  waitingVisible = true;
  const onVisibility = () => {
    if (isHidden()) return;
    document.removeEventListener('visibilitychange', onVisibility);
    waitingVisible = false;
    if (useLoopStore.getState().playing && !timer) timer = setTimeout(tick, 0);
  };
  document.addEventListener('visibilitychange', onVisibility);
}

function tick(): void {
  timer = null;
  const loop = useLoopStore.getState();
  if (!loop.playing) return;
  if (isHidden()) {
    waitVisible();
    return;
  }
  const { frames, index, setIndex } = useTimeStore.getState();
  const n = frames.length;
  if (n < 2) {
    stopPlayback();
    return;
  }
  let next = nextPlaybackIndex(index, n, direction, loop.mode);
  // Pula quadros sem nenhuma imagem (falharam em todas as camadas): sem piscar vazio.
  for (let k = 0; k < n && isFrameUnavailable(next.index); k++) {
    next = nextPlaybackIndex(next.index, n, next.direction, loop.mode);
  }
  if (!isFrameReady(next.index)) {
    setBuffering(true);
    timer = setTimeout(tick, BUFFER_POLL_MS);
    return;
  }
  setBuffering(false);
  direction = next.direction;
  setIndex(next.index);
  let delay = 1000 / Math.max(0.5, loop.fps);
  if (next.index === n - 1) delay += loop.dwellMs;
  timer = setTimeout(tick, delay);
}

export function isPlaying(): boolean {
  return useLoopStore.getState().playing;
}

/** Sentido atual da reprodução (no "vai e volta" alterna nas pontas). */
export function getPlaybackDirection(): 1 | -1 {
  return direction;
}

// ---------------------------------------------------------------------------
// Trava de exportação
// ---------------------------------------------------------------------------

/**
 * Trava (ou destrava) o loop durante uma exportação. Enquanto travado:
 * atalhos de teclado e controles da barra de tempo ficam desativados, o
 * quadro parado não é trocado por versão mais nítida e a atualização
 * automática NÃO altera `frames`/`range` (a mudança é aplicada ao destravar).
 * As funções deste módulo (setIndex, play, pause…) continuam funcionando
 * para quem exporta.
 */
export function setExportLock(locked: boolean): void {
  if (useLoopStore.getState().exportLocked !== locked) useLoopStore.setState({ exportLocked: locked });
}

export function isExportLocked(): boolean {
  return useLoopStore.getState().exportLocked;
}

/** Hook React: true enquanto a exportação trava o loop. */
export function useExportLock(): boolean {
  return useLoopStore((s) => s.exportLocked);
}

// ---------------------------------------------------------------------------
// Reprodução
// ---------------------------------------------------------------------------

/** O usuário (ou a exportação) já mexeu na reprodução — desliga o recuo automático da 1ª carga (startup.ts). */
let interacted = false;

export function hasUserInteracted(): boolean {
  return interacted;
}

function startPlayback(): boolean {
  const { frames, index, setIndex } = useTimeStore.getState();
  if (frames.length < 2) return false;
  // O primeiro play libera o download do loop inteiro: até aqui o portal
  // estava parado na medição mais recente, com só esse quadro baixado. A
  // reprodução espera cada quadro seguinte chegar ("carregando") e o resto
  // baixa enquanto toca.
  if (!useLoopStore.getState().loopArmed) useLoopStore.setState({ loopArmed: true });
  clearTimer();
  if (index >= frames.length - 1) setIndex(0);
  direction = 1;
  useLoopStore.setState({ playing: true, buffering: false });
  timer = setTimeout(tick, 1000 / Math.max(0.5, useLoopStore.getState().fps));
  return true;
}

/** Lembra se o usuário deixou tocando (recarregar a página retoma; ver startup.ts). */
function rememberPlaying(on: boolean): void {
  try {
    if (on) globalThis.localStorage?.setItem(PLAYING_KEY, '1');
    else globalThis.localStorage?.removeItem(PLAYING_KEY);
  } catch {
    // sem armazenamento: só não lembra
  }
}

/** A última visita terminou com o player tocando? */
export function wasPlaying(): boolean {
  try {
    return globalThis.localStorage?.getItem(PLAYING_KEY) === '1';
  } catch {
    return false;
  }
}

export function play(): void {
  interacted = true;
  rememberPlaying(true);
  startPlayback();
}

function stopPlayback(): void {
  clearTimer();
  if (useLoopStore.getState().playing || useLoopStore.getState().buffering) {
    useLoopStore.setState({ playing: false, buffering: false });
  }
}

/**
 * Para sem mexer na preferência "estava tocando" (o modo "Próximos dias"
 * suspende o loop das 48 h e o retoma ao sair; `pause` apagaria a lembrança).
 */
export function suspend(): void {
  stopPlayback();
}

export function pause(): void {
  interacted = true;
  rememberPlaying(false);
  stopPlayback();
}

export function togglePlay(): void {
  if (isPlaying()) pause();
  else play();
}

/**
 * Vai para o quadro `index` (limitado às pontas) e pausa. Antes do primeiro
 * play isso NÃO libera o loop inteiro: o pré-carregamento baixa só o quadro
 * pedido (sob demanda), e os já baixados ficam.
 */
export function goToFrame(index: number): void {
  pause();
  const { frames, setIndex } = useTimeStore.getState();
  if (frames.length === 0) return;
  setIndex(Math.max(0, Math.min(frames.length - 1, index)));
}

export function stepFrame(delta: number): void {
  goToFrame(useTimeStore.getState().index + delta);
}

export function goToFirst(): void {
  goToFrame(0);
}

export function goToLast(): void {
  goToFrame(useTimeStore.getState().frames.length - 1);
}
